import type {
  MenuCategory,
  MenuItem,
  MenuItemModifierGroup,
  ModifierGroup,
  ModifierOption,
} from '@adop/shared';

/** Everything needed to copy a menu between locations. */
export interface MenuSnapshot {
  categories: MenuCategory[];
  items: MenuItem[];
  groups: ModifierGroup[];
  options: ModifierOption[];
  links: MenuItemModifierGroup[];
}

/** A link row also needs the tenancy columns to be inserted. */
export type MenuCopyLink = MenuItemModifierGroup & { restaurant_id: string; location_id: string };

/** Options carry location_id in the database, even though the shared view omits it. */
export type MenuCopyOption = ModifierOption & { location_id: string };

export interface MenuCopyPlan {
  categories: MenuCategory[];
  items: MenuItem[];
  groups: ModifierGroup[];
  options: MenuCopyOption[];
  links: MenuCopyLink[];
}

export interface MenuCopyScope {
  categories: boolean;
  items: boolean;
  modifiers: boolean;
}

function sortBy<T extends { sort_order: number }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => a.sort_order - b.sort_order);
}

/**
 * Pure id-remapping for "Copy menu from another location". Produces rows for the
 * target location with fresh ids, preserving order, availability, images and tags.
 * Nothing security-sensitive is copied (the DB regenerates timestamps and the
 * caller supplies the target location/restaurant ids).
 */
export function planMenuCopy(
  source: MenuSnapshot,
  target: { restaurantId: string; locationId: string },
  scope: MenuCopyScope,
  newId: () => string,
): MenuCopyPlan {
  const categoryIdMap = new Map<string, string>();
  const groupIdMap = new Map<string, string>();
  const itemIdMap = new Map<string, string>();

  const categories = scope.categories
    ? sortBy(source.categories).map((category) => {
        const id = newId();
        categoryIdMap.set(category.id, id);
        return {
          ...category,
          id,
          restaurant_id: target.restaurantId,
          location_id: target.locationId,
        };
      })
    : [];

  const groups = scope.modifiers
    ? source.groups.map((group) => {
        const id = newId();
        groupIdMap.set(group.id, id);
        return {
          ...group,
          id,
          restaurant_id: target.restaurantId,
          location_id: target.locationId,
        };
      })
    : [];

  const options = scope.modifiers
    ? source.options
        .filter((option) => groupIdMap.has(option.group_id))
        .map((option) => ({
          ...option,
          id: newId(),
          group_id: groupIdMap.get(option.group_id) as string,
          restaurant_id: target.restaurantId,
          location_id: target.locationId,
        }))
    : [];

  const items = scope.items
    ? sortBy(source.items)
        .filter((item) => categoryIdMap.has(item.category_id))
        .map((item) => {
          const id = newId();
          itemIdMap.set(item.id, id);
          return {
            ...item,
            id,
            category_id: categoryIdMap.get(item.category_id) as string,
            restaurant_id: target.restaurantId,
            location_id: target.locationId,
          };
        })
    : [];

  const links = scope.modifiers
    ? source.links
        .filter((link) => itemIdMap.has(link.item_id) && groupIdMap.has(link.group_id))
        .map((link) => ({
          ...link,
          item_id: itemIdMap.get(link.item_id) as string,
          group_id: groupIdMap.get(link.group_id) as string,
          restaurant_id: target.restaurantId,
          location_id: target.locationId,
        }))
    : [];

  return { categories, items, groups, options, links };
}
